import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import Seo, { breadcrumbs, webPageSchema, serviceSchema } from "../components/Seo";
import LeadForm from "../components/LeadForm";
import QuickTradeEstimator from "../components/QuickTradeEstimator";
import CtaCallout from "../components/CtaCallout";
import { SectionHeader, Disclaimer } from "../components/UI";
import AboutScottBlurb from "../components/AboutScottBlurb";
import { siteConfig, disclaimers } from "../config";

export default function ValueMyTrade() {
  const [sp] = useSearchParams();
  const targetVehicle = sp.get("for") || "";
  const [tradeEstimate, setTradeEstimate] = useState(null);

  return (
    <main id="main-content">
      <Seo
        title="Trade In Your Vehicle in Central Ohio | Scott Sells Chevys"
        description="Start your trade-in conversation with Scott Hall. Send the basics on your trade — year, miles, condition, payoff — and Scott reviews and follows up."
        canonicalPath="/value-my-trade"
        jsonLd={[
          breadcrumbs([{ name: "Home", path: "/" }, { name: "Value My Trade", path: "/value-my-trade" }]),
          webPageSchema({ name: "Value My Trade", path: "/value-my-trade", description: "Send your vehicle trade-in details to Scott for a personal review." }),
          serviceSchema({
            name: "Vehicle Trade-In Help",
            serviceType: "Trade-in valuation assistance",
            path: "/value-my-trade",
            description: "Scott reviews your vehicle details and payoff and helps explain what affects a real trade number in Central Ohio.",
          }),
        ]}
      />

      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-16">
          <div className="ssc-eyebrow">Trade-In</div>
          <h1 className="ssc-display text-4xl md:text-6xl font-black mt-2">Thinking About Trading?<br/><span className="ssc-grad-text">Start Here.</span></h1>
          <p className="mt-4 text-[var(--ssc-silver)] max-w-2xl text-lg">Send me the basics on your trade and I'll help you understand the next step.</p>
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <AboutScottBlurb
              heading="Personal Trade Help from Scott"
              text="Scott has over 15 years of experience helping customers understand trade-ins, payoffs, vehicle condition, and next steps. Online trade values are only a starting point, but the right details can make the conversation much easier."
              testId="trade-about-scott"
            />
            <QuickTradeEstimator testIdPrefix="trade-quick" onValue={(r) => setTradeEstimate(r.dealer_trade_high)} />

            <div className="ssc-card">
              <h2 className="text-2xl font-black text-[var(--ssc-navy)]">Send Full Trade Details to Scott</h2>
              <p className="mt-2 text-[var(--ssc-ink-2)]">Photos and notes help Scott give you a more accurate review.</p>
              <div className="mt-4">
                <LeadForm
                  leadType="trade_value"
                  leadSource="Value My Trade Page"
                  testIdPrefix="trade-lead"
                  fields={[
                    { name: "name", label: "Name", required: true, autoComplete: "name" },
                    { name: "phone", label: "Phone", inputMode: "tel", autoComplete: "tel" },
                    { name: "email", label: "Email", type: "email", autoComplete: "email" },
                    { name: "trade_year", label: "Year", inputMode: "numeric" },
                    { name: "trade_make", label: "Make" },
                    { name: "trade_model", label: "Model" },
                    { name: "trade_trim", label: "Trim" },
                    { name: "trade_mileage", label: "Mileage", inputMode: "numeric" },
                    { name: "trade_vin", label: "VIN" },
                    { name: "trade_zip", label: "ZIP Code", inputMode: "numeric" },
                    { name: "trade_payoff", label: "Payoff amount (if any)", inputMode: "numeric" },
                    { name: "loan_or_lease", label: "Loan or Lease", type: "select", options: ["Owned", "Loan", "Lease"] },
                    { name: "trade_condition", label: "Condition", type: "select", options: ["Outstanding", "Clean", "Average", "Rough"] },
                    { name: "warning_lights", label: "Any warning lights?", type: "select", options: ["No", "Yes"] },
                    { name: "accidents", label: "Any accidents?", type: "select", options: ["No", "Yes"] },
                    { name: "target_vehicle", label: "What are you hoping to trade into?", placeholder: targetVehicle, full: true },
                    { name: "trade_notes", label: "Notes", type: "textarea", full: true, placeholder: "Anything else I should know? (Photos can be sent via text after submitting.)" },
                  ]}
                  hidden={{
                    trade_estimate: tradeEstimate || null,
                    cta_clicked: "Trade Submit",
                  }}
                  submitLabel="Send Trade Request to Scott"
                />
                <div className="mt-3"><Disclaimer text={disclaimers.trade} /></div>
              </div>
            </div>
          </div>

          <aside className="space-y-5">
            <CtaCallout
              testId="trade-cta-callout"
              variant="white"
              heading="Prefer to text Scott?"
              sub="Send year, make, model, miles, and payoff — he'll reply with next steps."
              actions={[
                { key: "text", label: "Text Scott", icon: "text", href: siteConfig.scott.phoneSms },
                { key: "find", label: "Find Next Vehicle", icon: "find", to: "/find-me-a-vehicle" },
                { key: "payment", label: "Payment Help", icon: "payment", to: "/payment-help" },
              ]}
            />
            <div className="ssc-card">
              <h3 className="font-black text-[var(--ssc-navy)] text-lg">What affects trade value</h3>
              <ul className="mt-3 text-sm text-[var(--ssc-ink-2)] space-y-1 list-disc pl-5">
                <li>Mileage & condition</li>
                <li>Service & accident history</li>
                <li>Local market demand</li>
                <li>Original equipment & options</li>
                <li>Title status & payoff</li>
                <li>Vehicle inspection</li>
              </ul>
            </div>
            <div className="ssc-card">
              <h3 className="font-black text-[var(--ssc-navy)] text-lg">Photo checklist</h3>
              <ul className="mt-3 text-sm text-[var(--ssc-ink-2)] space-y-1 list-disc pl-5">
                <li>Front, back, both sides</li>
                <li>Interior</li>
                <li>Odometer reading</li>
                <li>VIN sticker</li>
                <li>Tires</li>
                <li>Any damage</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
